/**
 * client-side Single Page Application (SPA) Router
 * Handles hash changes, authentication guards, and page lifecycle mounting.
 */

import { auth } from './firebase.js';
import { currentUserRole } from './auth.js';

// Configuration of routes and page imports
const routes = {
  'login': { module: () => import('./pages/login.js'), requiresAuth: false },
  'dashboard': { module: () => import('./pages/dashboard.js'), requiresAuth: true },
  'doctors': { module: () => import('./pages/doctors.js'), requiresAuth: true },
  'sales': { module: () => import('./pages/sales.js'), requiresAuth: true },
  'investment': { module: () => import('./pages/investment.js'), requiresAuth: true },
  'reports': { module: () => import('./pages/reports.js'), requiresAuth: true },
  'settings': { module: () => import('./pages/settings.js'), requiresAuth: true, requiresAdmin: true },
  'profile': { module: () => import('./pages/profile.js'), requiresAuth: true },
  'approvals': { module: () => import('./pages/approvals.js'), requiresAuth: true, requiresAdmin: true },
  'stockists': { module: () => import('./pages/stockists.js'), requiresAuth: true },
  'chemists': { module: () => import('./pages/chemists.js'), requiresAuth: true },
  'dcr': { module: () => import('./pages/dcr.js'), requiresAuth: true },
  'mtp': { module: () => import('./pages/mtp.js'), requiresAuth: true }
};

let currentPageInstance = null;

/**
 * Parses current location hash to locate matched route path
 * @returns {string}
 */
export function getRoute() {
  const hash = window.location.hash.slice(1) || '';
  const cleanPath = hash.split('?')[0]; // strip query strings
  return cleanPath || 'dashboard'; // fallback to dashboard if hash is empty
}

/**
 * Handle navigation to specific routes
 * @param {string} hashRoute 
 */
export function navigateTo(hashRoute) {
  window.location.hash = hashRoute;
}

/**
 * SPA Router main entry point
 */
export async function router() {
  const routePath = getRoute();
  const matchedRoute = routes[routePath] || { module: () => import('./pages/dashboard.js'), requiresAuth: true }; // fallback to dashboard
  
  // 1. Auth Guard Check
  const currentUser = auth.currentUser;
  
  // If route requires authentication and user is not logged in:
  if (matchedRoute.requiresAuth && !currentUser) {
    console.warn(`Unauthenticated access to #${routePath}. Redirecting to login.`);
    navigateTo('login');
    return;
  }

  // If route requires admin and user is not admin:
  if (matchedRoute.requiresAdmin && currentUserRole !== 'Admin') {
    console.warn(`Unauthorized admin access to #${routePath}. Redirecting to dashboard.`);
    navigateTo('dashboard');
    return;
  }
  
  // If user is already logged in and attempts to access login page:
  if (routePath === 'login' && currentUser) {
    navigateTo('dashboard');
    return;
  }

  // 2. Adjust Layout Visibilities
  const appWrapper = document.getElementById('app-wrapper');
  const sidebar = document.getElementById('sidebar-container');
  const topbar = document.getElementById('topbar-container');
  const mainContent = document.querySelector('.main-content-wrapper');

  if (routePath === 'login') {
    appWrapper.classList.remove('d-none');
    if (sidebar) sidebar.classList.add('d-none');
    if (topbar) topbar.classList.add('d-none');
    if (mainContent) {
      mainContent.style.marginLeft = '0';
      mainContent.style.width = '100%';
    }
    document.body.classList.remove('sidebar-open', 'sidebar-collapsed');
  } else {
    appWrapper.classList.remove('d-none');
    if (sidebar) sidebar.classList.remove('d-none');
    if (topbar) topbar.classList.remove('d-none');
    if (mainContent) {
      mainContent.style.marginLeft = '';
      mainContent.style.width = '';
    }
  }


  // 3. Dynamic Page Mounting
  const routerView = document.getElementById('router-view');
  
  // Call destroy hook on preceding page if it exists
  if (currentPageInstance && typeof currentPageInstance.destroy === 'function') {
    currentPageInstance.destroy();
  }

  try {
    // Show spinner in inner router view during loading
    routerView.innerHTML = `
      <div class="d-flex justify-content-center align-items-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading page...</span>
        </div>
      </div>
    `;

    // Load page module dynamically
    const pageModule = await matchedRoute.module();
    currentPageInstance = pageModule;

    // Render HTML content
    routerView.innerHTML = pageModule.render();

    // Fire post-render event bindings
    if (typeof pageModule.afterRender === 'function') {
      await pageModule.afterRender();
    }

    // Trigger update of Active Link indicator in the sidebar navigation
    updateActiveSidebarLink(routePath);
    
    // Update breadcrumbs in topbar
    updateTopbarBreadcrumbs(routePath);

  } catch (error) {
    console.error(`Failed to load page module [${routePath}]:`, error);
    routerView.innerHTML = `
      <div class="alert alert-danger py-4 my-4 card-custom text-center">
        <i class="bi bi-exclamation-triangle-fill fs-1 text-danger mb-3 d-block"></i>
        <h4 class="fw-bold font-heading">Page Load Error</h4>
        <p class="text-muted mb-3">Something went wrong while rendering this section.</p>
        <button class="btn btn-primary btn-sm" onclick="location.reload()">Reload Page</button>
      </div>
    `;
  }
}

/**
 * Highlights active link in sidebar navigation matching the active route path
 * @param {string} route 
 */
function updateActiveSidebarLink(route) {
  const links = document.querySelectorAll('.sidebar-link');
  links.forEach(link => {
    const linkRoute = link.getAttribute('data-route');
    if (linkRoute === route) {
      link.classList.add('active');
    } else {
      link.classList.remove('active');
    }
  });
}

/**
 * Updates page breadcrumb indicator inside top navbar
 * @param {string} route 
 */
function updateTopbarBreadcrumbs(route) {
  const title = route.charAt(0).toUpperCase() + route.slice(1);
  const breadcrumbNode = document.getElementById('topbar-breadcrumb-active');
  if (breadcrumbNode) {
    breadcrumbNode.textContent = title;
  }
}
